// Libraries imports
import { useState } from 'react'
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import SettingTextField from "../../../components/SettingTextField"
import SettingSelectField from "../../../components/SettingSelectField";

// Constants and utils imports
import {
    RESTAURANT_ADMIN_ROLE_ID,
    RESTAURANT_USER_ROLE_ID,
    USER_ROLE_LIST,
} from "../../../constants";
import { THEME_MODE } from "../../../constants/Theme";
import { isEmailValid } from "../../../lib/utils";
import { ModalModes } from "./constants";

const UserFields = {
    userRole: "userRole",
    username: "username",
    email: "email",
    phoneNumber: "phoneNumber",
    password: "password",
    confirmPassword: "confirmPassword",
    pin: "pin",
    confirmPin: "confirmPin",
}

const AddUser = ({ open, onClose, userData, mode, onSave, loading, themeMode }) => {
    const theme_mode = THEME_MODE[themeMode ?? "light"];

    const [formData, setFormData] = useState({
        userRole: userData?._idUserRoles ?? RESTAURANT_USER_ROLE_ID,
        username: userData?.name ?? "",
        email: userData?.email ?? "",
        phoneNumber: userData?.phoneNumber ?? "",
        password: "",
        confirmPassword: "",
        pin: userData?.pin ?? "",
        confirmPin: userData?.pin ?? "",
    });

    const [errors, setErrors] = useState(new Map());
    const [changes, setChanges] = useState(new Set());

    const closeModal = () => {
        // Do not close the modal when we are saving the form
        if (loading) return;
        onClose();
    }

    const onSubmit = () => {
        if (errors.size) return;

        let toServerData = {};

        if (mode === ModalModes.CHANGE_ROLE) {
            // For role change, only send role-related data
            toServerData._idUserRoles = formData.userRole;
            toServerData.userRole = formData.userRole === RESTAURANT_ADMIN_ROLE_ID
                ? "Admin"
                : formData.userRole === RESTAURANT_USER_ROLE_ID
                    ? "User"
                    : "";
            toServerData = { ...userData, ...toServerData };
        } else {
            // Regular add/edit logic
            toServerData.name = formData.username.trim();
            toServerData.email = null;
            toServerData.phoneNumber = null;
            toServerData.pin = formData.pin.trim();
            toServerData._idUserRoles = formData.userRole;
            toServerData.userRole = formData.userRole === RESTAURANT_ADMIN_ROLE_ID
                ? "Admin"
                : formData.userRole === RESTAURANT_USER_ROLE_ID
                    ? "User"
                    : "";

            if (formData.userRole === RESTAURANT_ADMIN_ROLE_ID) {
                toServerData.email = formData.email.trim();
                toServerData.phoneNumber = formData.phoneNumber.trim();

                if (mode === ModalModes.EDIT) {
                    if (formData.password.trim()) {
                        toServerData = {
                            ...toServerData,
                            password: formData.password.trim(),
                        };
                    }
                } else {
                    toServerData = {
                        ...toServerData,
                        password: formData.password.trim(),
                    };
                }
            } else {
                toServerData.password = null;
            }

            if (mode === ModalModes.EDIT) toServerData = { ...userData, ...toServerData };
        }

        onSave(toServerData);
    };

    const handleInputChange = (field) => (val) => {
        // Enforce number input for numeric fields
        if ([UserFields.phoneNumber, UserFields.pin, UserFields.confirmPin].includes(field)) {
            val = val.replace(/[^0-9]/g, '');
        }

        const newFormData = { ...formData }

        // Update errors
        const newErrors = new Map(errors);

        // Handle primary field error
        const fieldError = checkError(field, val, newFormData.userRole, newFormData);
        if (fieldError) newErrors.set(field, fieldError);
        else newErrors.delete(field);

        // Cross-field validation (trigger confirm field check too)
        if (field === UserFields.pin) if (val === newFormData.confirmPin) newErrors.delete(UserFields.confirmPin);
        if (field === UserFields.confirmPin) if (val === newFormData.pin) newErrors.delete(UserFields.pin);
        if (field === UserFields.password) if (val === newFormData.confirmPassword) newErrors.delete(UserFields.confirmPassword);
        if (field === UserFields.confirmPassword) if (val === newFormData.password) newErrors.delete(UserFields.password);

        // Handling case to remove admin-user related errors when the role is changed from admin to user
        if (field === UserFields.userRole) {
            if (val === RESTAURANT_USER_ROLE_ID) {
                newErrors.delete(UserFields.email);
                newErrors.delete(UserFields.phoneNumber);
                newErrors.delete(UserFields.password);
                newErrors.delete(UserFields.confirmPassword);

                changes.delete(UserFields.email);
                changes.delete(UserFields.phoneNumber);
                changes.delete(UserFields.password);
                changes.delete(UserFields.confirmPassword);

                newFormData[UserFields.email] = "";
                newFormData[UserFields.phoneNumber] = "";
                newFormData[UserFields.password] = "";
                newFormData[UserFields.confirmPassword] = "";
            } else {
                // Only set required errors for ADD mode or when fields are empty
                if (mode === ModalModes.ADD || !userData?.email) {
                    if (!newFormData[UserFields.email]) {
                        newErrors.set(UserFields.email, "Email is required");
                    }
                }
                if (mode === ModalModes.ADD || !userData?.phoneNumber) {
                    if (!newFormData[UserFields.phoneNumber]) {
                        newErrors.set(UserFields.phoneNumber, "Phone Number is required");
                    }
                }
                if (mode === ModalModes.ADD) {
                    if (!newFormData[UserFields.password]) {
                        newErrors.set(UserFields.password, "Password is required");
                    }
                    if (!newFormData[UserFields.confirmPassword]) {
                        newErrors.set(UserFields.confirmPassword, "Confirm Password is required");
                    }
                }
            }
        }

        setErrors(newErrors);

        // Update the form state
        newFormData[field] = val;
        setFormData(newFormData);

        // Track changes
        const isChanged = hasInputChanged(field, val);
        const newChangeSet = new Set(changes);
        if (isChanged) newChangeSet.add(field);
        else newChangeSet.delete(field);
        setChanges(newChangeSet);
    };

    const checkError = (field, value, userRole, formData) => {
        const isAdmin = userRole === RESTAURANT_ADMIN_ROLE_ID;

        switch (field) {
            case UserFields.username:
                return value.trim() === "" ? "Username is required" : null;

            case UserFields.email:
                if (!isAdmin) return null;
                // For edit mode, only validate if there's a value or if it's required
                if (mode === ModalModes.EDIT && value.trim() === "" && userData?.email) {
                    return null; // Allow empty email in edit mode if user already has email
                }
                return value.trim() === ""
                    ? "Email is required"
                    : !isEmailValid(value)
                        ? "Email is invalid"
                        : null;

            case UserFields.phoneNumber:
                if (!isAdmin) return null;
                // For edit mode, only validate if there's a value or if it's required
                if (mode === ModalModes.EDIT && value.trim() === "" && userData?.phoneNumber) {
                    return null; // Allow empty phone in edit mode if user already has phone
                }
                return value.trim() === "" ? "Phone Number is required" : null;

            case UserFields.password:
                if (!isAdmin) return null;
                // For edit mode, password is optional
                if (mode === ModalModes.EDIT && value.trim() === "") {
                    return null; // Password is optional in edit mode
                }
                return value.trim() === ""
                    ? "Password is required"
                    : value.length < 8
                        ? "Password must contain at least 8 characters"
                        : formData.confirmPassword && formData.confirmPassword !== value
                            ? "Passwords do not match"
                            : null;

            case UserFields.confirmPassword:
                if (!isAdmin) return null;
                // For edit mode, confirm password is optional
                if (mode === ModalModes.EDIT && value.trim() === "") {
                    return null; // Confirm password is optional in edit mode
                }
                return value.trim() === ""
                    ? "Confirm Password is required"
                    : formData.password && formData.password !== value
                        ? "Passwords do not match"
                        : null;

            case UserFields.pin:
                return value.trim() === ""
                    ? "Pin is required"
                    : value.length < 4
                        ? "Pin should have at least 4 digits"
                        : formData.confirmPin && formData.confirmPin !== value
                            ? "Pins do not match"
                            : null;

            case UserFields.confirmPin:
                return value.trim() === ""
                    ? "Confirm Pin is required"
                    : formData.pin && formData.pin !== value
                        ? "Pins do not match"
                        : null;

            default:
                return null;
        }
    };

    const hasInputChanged = (field, value) => {
        if (field === UserFields.userRole) {
            const originalValue = userData?._idUserRoles ?? "";
            return value !== originalValue;
        }
        const originalValue = userData?.[field] ?? "";
        return value !== originalValue;
    }

    const isSaveBtnDisabled = (() => {
        if (errors.size > 0 || loading) return true;

        if (mode === ModalModes.CHANGE_ROLE) {
            // For role change, just check if role has changed
            return formData.userRole === userData?._idUserRoles;
        }

        if (mode === ModalModes.EDIT) {
            // For edit mode, allow save if there are any changes
            return changes.size === 0;
        }

        if (changes.size === 0) return true;

        if (mode === ModalModes.ADD) {
            if (formData.userRole === RESTAURANT_USER_ROLE_ID) {
                if (formData.username && formData.pin && formData.confirmPin) return false;
                else return true;
            } else {
                // There are currently 8 fields in the form. And all of them are required
                // (even the drop down value should change from the default user roles to admin roles); 
                if (changes.size === 8) return false;
                return true;
            }
        } else return false;
    })();

    return (
        <Dialog
            open={open}
            onClose={closeModal}
            sx={styles.mainDialog} >
            <Grid p={4}>
                <DialogTitle sx={styles.confirmTitleText}>
                    {mode === ModalModes.CHANGE_ROLE ? "Change User Role" : `${mode} Staff`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description" sx={styles.confirmContentText}>
                        <Grid container spacing={2} p={0}>
                            <Grid item xs={12}>
                                <SettingSelectField
                                    editable={!loading}
                                    value={formData.userRole}
                                    setValue={handleInputChange(UserFields.userRole)}
                                    backgroundColor={theme_mode.inputBg2}
                                    label={"User Role"}
                                    labelStyle={styles.labelStyle}
                                    required={true}
                                    variant={"filled"}
                                    menuList={USER_ROLE_LIST}
                                    error={errors.get(UserFields.userRole)}
                                    errorStyles={styles.errorStyle}
                                    sx={{ textAlign: "left" }}
                                />
                            </Grid>

                            {mode !== ModalModes.CHANGE_ROLE && (
                                <>
                                    <Grid item xs={12}>
                                        <SettingTextField
                                            editable={!loading}
                                            value={formData.username}
                                            setValue={handleInputChange(UserFields.username)}
                                            backgroundColor={theme_mode.inputBg2}
                                            label={"User Name"}
                                            labelStyle={styles.labelStyle}
                                            required={true}
                                            variant={"filled"}
                                            error={errors.get(UserFields.username)}
                                            errorStyles={styles.errorStyle}
                                        />
                                    </Grid>

                                    {formData.userRole === RESTAURANT_ADMIN_ROLE_ID && (
                                        <>
                                            <Grid item xs={12}>
                                                <SettingTextField
                                                    editable={!loading}
                                                    value={formData.email}
                                                    setValue={handleInputChange(UserFields.email)}
                                                    backgroundColor={theme_mode.inputBg2}
                                                    label={"Email"}
                                                    labelStyle={styles.labelStyle}
                                                    required={true}
                                                    variant={"filled"}
                                                    type={"email"}
                                                    error={errors.get(UserFields.email)}
                                                    errorStyles={styles.errorStyle}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SettingTextField
                                                    editable={!loading}
                                                    value={formData.phoneNumber}
                                                    setValue={handleInputChange(UserFields.phoneNumber)}
                                                    backgroundColor={theme_mode.inputBg2}
                                                    label={"Phone Number"}
                                                    labelStyle={styles.labelStyle}
                                                    required={true}
                                                    variant={"filled"}
                                                    type={"number"}
                                                    error={errors.get(UserFields.phoneNumber)}
                                                    errorStyles={styles.errorStyle}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12}>
                                        <SettingTextField
                                            editable={!loading}
                                            value={formData.pin}
                                            setValue={handleInputChange(UserFields.pin)}
                                            backgroundColor={theme_mode.inputBg2}
                                            label={"Pin"}
                                            labelStyle={styles.labelStyle}
                                            required={true}
                                            variant={"filled"}
                                            type={"number"}
                                            error={errors.get(UserFields.pin)}
                                            errorStyles={styles.errorStyle}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <SettingTextField
                                            editable={!loading}
                                            value={formData.confirmPin}
                                            setValue={handleInputChange(UserFields.confirmPin)}
                                            backgroundColor={theme_mode.inputBg2}
                                            label={"Confirm Pin"}
                                            labelStyle={styles.labelStyle}
                                            required={true}
                                            variant={"filled"}
                                            type={"number"}
                                            error={errors.get(UserFields.confirmPin)}
                                            errorStyles={styles.errorStyle}
                                        />
                                    </Grid>

                                    {formData.userRole === RESTAURANT_ADMIN_ROLE_ID && (
                                        <>
                                            <Grid item xs={12}>
                                                <SettingTextField
                                                    editable={!loading}
                                                    value={formData.password}
                                                    setValue={handleInputChange(UserFields.password)}
                                                    backgroundColor={theme_mode.inputBg2}
                                                    label={"Password"}
                                                    labelStyle={styles.labelStyle}
                                                    required={true}
                                                    variant={"filled"}
                                                    type={"password"}
                                                    error={errors.get(UserFields.password)}
                                                    errorStyles={styles.errorStyle}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SettingTextField
                                                    editable={!loading}
                                                    value={formData.confirmPassword}
                                                    setValue={handleInputChange(UserFields.confirmPassword)}
                                                    backgroundColor={theme_mode.inputBg2}
                                                    label={"Re Enter Password"}
                                                    labelStyle={styles.labelStyle}
                                                    required={true}
                                                    variant={"filled"}
                                                    type={"password"}
                                                    error={errors.get(UserFields.confirmPassword)}
                                                    errorStyles={styles.errorStyle}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </>
                            )}
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={styles.confirmBtnDialog}>
                    <Button sx={[styles.btnText, styles.noBtnText]}
                        onClick={closeModal}>
                        cancel
                    </Button>
                    <Button sx={[styles.btnText, styles.yesBtnText]}
                        disabled={isSaveBtnDisabled}
                        onClick={onSubmit}>
                        {loading ? "Processing..." : mode === ModalModes.CHANGE_ROLE ? "Change Role" : `${mode} Staff`}
                    </Button>
                </DialogActions>
            </Grid>
        </Dialog>
    )
}

export default AddUser;

const styles = {
    mainDialog: {
        '& .MuiDialog-paper': {
            borderRadius: 3,
            xs: {
                width: 'calc(100% - 2rem)',
            },
            sm: {
                width: '36rem'
            },
        },
    },
    labelStyle: {
        fontSize: 14,
        fontFamily: "GilroySemiBold",
        textAlign: "left"
    },
    errorStyle: {
        textAlign: 'left',
        fontSize: '0.75rem',
    },
    confirmTitleText: {
        fontFamily: 'GilroyBold',
        textAlign: 'center',
        fontSize: '1.5rem',
        textTransform: 'capitalize'
    },
    confirmContentText: {
        fontFamily: 'GilroyMedium',
        textAlign: 'center',
    },
    confirmBtnDialog: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '1.5rem'
    },
    btnText: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textTransform: 'capitalize',
        fontFamily: "GilroySemiBold",
        height: "2.5rem",
        padding: "0 18px",
        fontSize: "14px",
        cursor: "pointer",
        border: "2px solid #000000",
        borderRadius: "8px",
        flex: 1
    },
    noBtnText: {
        color: '#000000',
        '&:hover': {
            backgroundColor: '#ffffff !important',
        },
    },
    yesBtnText: {
        backgroundColor: '#000000',
        color: '#ffffff !important',
        borderRadius: 2,
        px: 2,
        '&:hover': {
            color: '#ffffff !important',
            backgroundColor: '#000000 !important',
        },
        '&:disabled': {
            opacity: 0.5
        }
    }
}
